import React, { useState } from 'react';
import { Button, Flex, Switch } from 'antd';
import {
  CalendarOutlined,
  CloseOutlined,
  DownOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import ConditionalTooltip from '@/components/ConditionalTooltip';
import { CustomParagraph } from '@/components';
import { RightTailArrowIcon } from '@/assets/icons/icons';
import type { FedExPickup } from '@/apis/fedexPickup';
import CancelPickupModal from './CancelPickupModal';
import { formatUsPhone, to12Hour } from './fedexPickupUtils';
import { PickupReadOnlyField, PickupSectionTitle } from './PickupFormField';

interface PickupDetailsPanelProps {
  pickup: FedExPickup;
  shipmentLabel?: string;
  onBack: () => void;
  onClose: () => void;
}

const LOCATION_LABELS: Record<FedExPickup['packageLocation'], string> = {
  FRONT: 'Front',
  REAR: 'Rear',
  SIDE: 'Side',
  NONE: 'None',
};

const trailingIcon = (icon: React.ReactNode) => (
  <span style={{ fontSize: 14, display: 'flex' }}>{icon}</span>
);

/** Static view of an already scheduled pickup; mirrors the schedule form's layout. */
const PickupDetailsPanel: React.FC<PickupDetailsPanelProps> = ({
  pickup,
  shipmentLabel,
  onBack,
  onClose,
}) => {
  const { t } = useTranslation('shipments');
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const { address, contact } = pickup;

  return (
    <Flex vertical className='h-full' style={{ overflow: 'hidden' }}>
      <Flex justify='space-between' align='center' style={{ padding: '4px 0' }}>
        <Flex align='center' gap={8}>
          <span className='c-pointer' onClick={onBack} title='Back'>
            <RightTailArrowIcon />
          </span>
          <span style={{ fontSize: 12 }}>
            {t('pickup.shipment', 'Shipment')}{' '}
            <strong>{shipmentLabel ?? ''}</strong>
          </span>
        </Flex>
        <span className='c-pointer' onClick={onClose} title='Close'>
          <CloseOutlined style={{ fontSize: 14 }} />
        </span>
      </Flex>
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 16 }}>
        <Flex vertical gap={24}>
          <CustomParagraph
            text={t('pickup.detailsTitle', 'Pickup details')}
            fontSize={24}
            fontWeight='700'
            marginTop='16px'
            marginBottom='0'
          />
          <PickupReadOnlyField
            label={t('pickup.confirmationCode', 'Confirmation code')}
            value={pickup.confirmationCode}
          />
          <Flex vertical gap={4}>
            <span style={{ fontSize: 14, fontWeight: 600, lineHeight: '16px' }}>
              {t('pickup.pickupFrom', 'Pickup from')}
            </span>
            <Flex
              align='flex-start'
              justify='space-between'
              gap={12}
              style={{
                border: '1px solid var(--border-color, #e2e2e2)',
                borderRadius: 8,
                padding: '12px 12px 12px 16px',
                fontSize: 12,
                lineHeight: '20px',
                color: 'var(--text-secondary)',
              }}
            >
              <div>
                {contact.company && (
                  <div style={{ fontWeight: 700 }}>{contact.company}</div>
                )}
                <div>{address.street}</div>
                <div>
                  {[address.city, address.state].filter(Boolean).join(', ')}{' '}
                  {address.postalCode}
                </div>
              </div>
              <Flex align='center' gap={12} style={{ opacity: 0.5 }}>
                <span
                  style={{ color: 'var(--text-primary)', lineHeight: '16px' }}
                >
                  {t('pickup.residential', 'Residential address')}
                </span>
                <Switch size='small' checked={address.residential} disabled />
              </Flex>
            </Flex>
          </Flex>

          <PickupSectionTitle
            text={t('pickup.sectionSchedule', 'Schedule')}
            heading
          />
          <PickupReadOnlyField
            label={t('pickup.date', 'Pickup date')}
            value={dayjs(pickup.pickupDate).format('MM/DD/YYYY')}
            trailing={trailingIcon(<CalendarOutlined />)}
          />
          <Flex gap={16}>
            <PickupReadOnlyField
              label={t('pickup.readyBy', 'Ready by')}
              value={to12Hour(pickup.readyTime)}
              trailing={trailingIcon(<DownOutlined />)}
            />
            <PickupReadOnlyField
              label={t('pickup.closesAt', 'Closes at')}
              value={to12Hour(pickup.closeTime)}
              trailing={trailingIcon(<DownOutlined />)}
            />
          </Flex>

          <PickupSectionTitle
            text={t('pickup.sectionContact', 'Contact')}
            heading
          />
          <Flex gap={16}>
            <PickupReadOnlyField
              label={t('pickup.nameLabel', 'Name')}
              value={contact.name}
            />
            <PickupReadOnlyField
              label={t('pickup.phoneNumber', 'Phone number')}
              value={contact.phone && formatUsPhone(contact.phone)}
            />
          </Flex>

          <PickupSectionTitle
            text={t('pickup.sectionPackage', 'Package')}
            heading
          />
          <Flex vertical gap={4}>
            <Flex gap={16}>
              <PickupReadOnlyField
                label={t('pickup.quantity', 'Quantity')}
                value={pickup.packageCount}
              />
              <PickupReadOnlyField
                label={t('pickup.packageLocation', 'Package location')}
                value={LOCATION_LABELS[pickup.packageLocation]}
                trailing={trailingIcon(<DownOutlined />)}
              />
            </Flex>
            <CustomParagraph
              text={t(
                'pickup.packageNote',
                'Matches the return label. Changing this may add surcharge'
              )}
              fontSize={10}
              color='var(--text-secondary)'
              marginBottom='0'
            />
          </Flex>
          <PickupReadOnlyField
            label={t('pickup.remarks', 'Remarks')}
            value={pickup.remarks}
          />
        </Flex>
      </div>
      <Flex vertical gap={8} style={{ padding: '16px 0 8px' }}>
        <ConditionalTooltip
          show={!pickup.canCancel}
          title={pickup.cancelBlockedReason}
        >
          <Button
            type='primary'
            block
            disabled={!pickup.canCancel}
            onClick={() => setConfirmingCancel(true)}
            style={{
              height: 48,
              fontWeight: 600,
              fontSize: 16,
              borderRadius: 8,
              background: pickup.canCancel ? '#000' : undefined,
            }}
          >
            {t('pickup.cancelPickupLower', 'Cancel pickup')}
          </Button>
        </ConditionalTooltip>
        <Button type='text' block onClick={onClose} style={{ height: 48 }}>
          {t('pickup.close', 'Close')}
        </Button>
      </Flex>
      <CancelPickupModal
        pickup={pickup}
        open={confirmingCancel}
        setOpen={setConfirmingCancel}
      />
    </Flex>
  );
};

export default PickupDetailsPanel;
